import { MedplumClient, MedplumRequestOptions } from "@medplum/core";
import type { Group } from "@medplum/fhirtypes";

import { FLAG_LIBRARY_GROUP_IDENTIFIER_SYSTEM } from "@/fhir/constants/identifier";
import { buildFlagLibraryGroup } from "@/flags/utils/flag-library-group";
import { setOrganizationAccountsDirectOnResource } from "@/utils/accounts";
import { joinReferenceString, joinTokenString } from "@/utils/medplum";

/**
 * Server side reads and writes for the per-practice Flag Library Group. See
 * `@/flags/utils/flag-library-group` for the resource builders.
 */

/**
 * Finds a practice's existing Flag Library Group, if any.
 *
 * Looks the Group up by identifier rather than by `_compartment`, because accounts indexing is not
 * immediately consistent after creation.
 *
 * @param medplum - The MedplumClient instance
 * @param organizationId - Practice Organization logical id
 * @param options - Request options, e.g. `{ cache: "no-cache" }` where a stale answer — including a
 *   cached miss for a practice whose Group was just created — would be wrong. Cached by default,
 *   which suits provisioning callers that create the Group themselves moments later.
 */
export async function fetchFlagLibraryGroup(
  medplum: MedplumClient,
  organizationId: string,
  options?: MedplumRequestOptions,
): Promise<Group | undefined> {
  const organizationReference = joinReferenceString("Organization", organizationId);
  const identifier = joinTokenString(FLAG_LIBRARY_GROUP_IDENTIFIER_SYSTEM, organizationReference);

  return await medplum.searchOne("Group", { identifier }, options);
}

/**
 * Creates the Flag Library Group for a practice unless it already exists. Does not set accounts —
 * callers that already own an accounts step (such as practice onboarding) do that themselves.
 *
 * @param medplum - The MedplumClient instance
 * @param organizationId - Practice Organization logical id
 * @returns The existing or newly created Group
 */
export async function findOrCreateFlagLibraryGroup(
  medplum: MedplumClient,
  organizationId: string,
): Promise<Group> {
  const existing = await fetchFlagLibraryGroup(medplum, organizationId);
  if (existing) return existing;

  const organizationReference = joinReferenceString("Organization", organizationId);

  return medplum.createResource(buildFlagLibraryGroup(organizationReference));
}

/**
 * Creates the Flag Library Group for a practice unless it already exists, then scopes it to the
 * practice compartment. Idempotent, so it is safe to re-run; use this from callers without their
 * own accounts step.
 *
 * @param medplum - The MedplumClient instance
 * @param organizationId - Practice Organization logical id
 * @returns The existing or newly created Group
 */
export async function ensureFlagLibraryGroup(
  medplum: MedplumClient,
  organizationId: string,
): Promise<Group> {
  const group = await findOrCreateFlagLibraryGroup(medplum, organizationId);

  await setOrganizationAccountsDirectOnResource(medplum, group, organizationId);

  return group;
}
